import { ChangeEvent, FC, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Heading,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { captureException } from '@sentry/react';
import * as ToastConfigs from '../../configs/toast';
import {
  FileDetailsFileFragment,
  usePiiJustifyByIdMutation,
} from '../../graphql/generated';

type Props = {
  isJustifying?: boolean;
  onConfirm: (comment: string) => void;
};

export const justifyConfirmationMessage =
  'Upon confirming, we will notify Resolution Life to release the file. When Resolution Life releases the file it will be available for download.';

export const PiiJustify: FC<Props> = ({ isJustifying, onConfirm }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [comment, setComment] = useState<string>('');
  const [errors, setErrors] = useState({ comment: false });

  const toggleAlertDialog = () => setIsOpen(!isOpen);

  const validate = () => {
    if (!comment) {
      return setErrors({ comment: true });
    }
    setErrors({ comment: false });
    return true;
  };

  const handlePiiApproval = () => {
    const isValid = validate();
    if (!isValid) {
      return;
    }
    toggleAlertDialog();
  };

  const handleConfirmation = () => {
    onConfirm(comment);
    toggleAlertDialog();
  };

  const handleChange = ({ target }: ChangeEvent<HTMLTextAreaElement>) =>
    setComment(target.value);

  const renderAlertDialog = (confirmationMessage: string) => {
    return (
      <AlertDialog
        isCentered
        isOpen={isOpen}
        onClose={toggleAlertDialog}
        leastDestructiveRef={undefined}
        data-testid="confirm-justification-dialog"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm PII Approval
            </AlertDialogHeader>
            <AlertDialogBody data-testid="confirm-justification-dialog-message">
              {confirmationMessage}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                onClick={toggleAlertDialog}
                data-testid="cancel-justification-button"
              >
                Cancel
              </Button>
              <Button
                ml={3}
                colorScheme="green"
                onClick={handleConfirmation}
                data-testid="confirm-justification-button"
              >
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    );
  };

  return (
    <Stack
      spacing={6}
      data-testid="pii-justification"
      maxWidth={{ sm: '100%', md: '65%' }}
    >
      <Stack spacing={6}>
        <Box>
          <Heading size="lg" mb="4">
            PII Detected
          </Heading>
          <Alert status="warning">
            <AlertIcon />
            This file may contain personally identifiable information (PII) and
            requires approval to confirm no PII is present in the file to be
            uploaded.
          </Alert>
        </Box>
      </Stack>

      <Stack spacing={4}>
        <Box>
          <Textarea
            value={comment}
            onChange={handleChange}
            isInvalid={errors.comment}
            placeholder="Review comment"
            data-testid="review-comment-textbox"
          />
          {errors.comment && (
            <Text color="red.500" data-testid="review-comment-validation-error">
              Review comment is a required field
            </Text>
          )}
        </Box>
        <Box>
          <Button
            colorScheme="yellow"
            loadingText="Approving"
            isLoading={isJustifying}
            onClick={handlePiiApproval}
            data-testid="justify-button"
          >
            Approve
          </Button>
        </Box>
      </Stack>
      {renderAlertDialog(justifyConfirmationMessage)}
    </Stack>
  );
};

export const PII_JUSTIFY_TOAST_ID = 'pii-justify-toast';

export const PiiJustifyContainer: FC<{
  file: FileDetailsFileFragment;
}> = ({ file }) => {
  const toast = useToast({ id: PII_JUSTIFY_TOAST_ID });
  const [justifyPiiById, { loading }] = usePiiJustifyByIdMutation();

  const handleJustify = async (comment: string) => {
    try {
      const result = await justifyPiiById({
        variables: {
          cedant: file.cedant,
          input: {
            id: file.id,
            comment,
          },
        },
        refetchQueries: ['GetFile'],
        awaitRefetchQueries: true,
      });

      if (result.data?.piiJustifyById) {
        toast({
          id: PII_JUSTIFY_TOAST_ID,
          title: 'Approved',
          status: 'success',
          duration: 4000,
          position: 'top',
        });
      }
    } catch (e) {
      toast({
        id: PII_JUSTIFY_TOAST_ID,
        ...ToastConfigs.problemCompletingRequest,
      });
      captureException(e);
    }
  };

  return <PiiJustify isJustifying={loading} onConfirm={handleJustify} />;
};